import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminUsers, getAdminAnalytics, getAdminSessions, sendAdminNotification, createProfile, getProfiles, deleteProfile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, ArrowLeft, Users, BarChart3, MessageSquare, Target, Plus, Trash2, Send } from 'lucide-react';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('analytics');
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Notification form
  const [notifMsg, setNotifMsg] = useState('');
  const [notifUserId, setNotifUserId] = useState('');

  // Profile form
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [pName, setPName] = useState('');
  const [pDesc, setPDesc] = useState('');
  const [pPersonality, setPPersonality] = useState('');
  const [pTone, setPTone] = useState('neutre');
  const [pDifficulty, setPDifficulty] = useState(2);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      const [analyticsRes, usersRes, sessionsRes, profilesRes] = await Promise.all([
        getAdminAnalytics(),
        getAdminUsers(),
        getAdminSessions(),
        getProfiles()
      ]);
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
      setSessions(sessionsRes.data);
      setProfiles(profilesRes.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleSendNotification = async () => {
    if (!notifMsg.trim()) return;
    try {
      await sendAdminNotification({ message: notifMsg, user_id: notifUserId || null, type: 'info' });
      setNotifMsg('');
      setNotifUserId('');
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateProfile = async () => {
    if (!pName.trim()) return;
    try {
      await createProfile({ name: pName, description: pDesc, personality: pPersonality, tone: pTone, difficulty: pDifficulty });
      setShowProfileForm(false);
      setPName(''); setPDesc(''); setPPersonality(''); setPTone('neutre'); setPDifficulty(2);
      const { data } = await getProfiles();
      setProfiles(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteProfile = async (id) => {
    try {
      await deleteProfile(id);
      setProfiles(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const tabs = [
    { id: 'analytics', label: 'Stats', icon: BarChart3 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'profiles', label: 'Profils', icon: Target },
    { id: 'notifs', label: 'Notifs', icon: MessageSquare },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="font-mono text-xl text-[#39FF14] animate-pulse">ADMIN LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <div className="bg-[#121214] border-b-2 border-[#39FF14]/30 p-4 flex items-center gap-3">
        <button data-testid="back-btn" onClick={() => navigate('/dashboard')} className="text-[#39FF14] hover:text-green-300 transition-colors">
          <ArrowLeft size={24} />
        </button>
        <Shield className="text-[#39FF14] drop-shadow-[0_0_6px_rgba(57,255,20,0.6)]" size={24} />
        <h1 className="font-heading text-2xl uppercase tracking-wider text-[#39FF14]">Admin</h1>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-800">
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-3 flex items-center justify-center gap-1 font-heading text-xs uppercase tracking-wider transition-all ${
              tab === t.id
                ? 'text-[#39FF14] border-b-2 border-[#39FF14] bg-[#39FF14]/5'
                : 'text-[#71717A] hover:text-white'
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      <div className="max-w-lg mx-auto p-4">
        {/* Analytics Tab */}
        {tab === 'analytics' && analytics && (
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'USERS', value: analytics.total_users, color: '#00AEEF' },
              { label: 'SESSIONS', value: analytics.total_sessions, color: '#E61C24' },
              { label: 'COMPLETED', value: analytics.completed_sessions, color: '#39FF14' },
              { label: 'AVG SCORE', value: analytics.avg_score, color: '#FFF200' },
              { label: 'MAX SCORE', value: analytics.max_score, color: '#FF333B' },
              { label: 'ACTIVE NOW', value: `${analytics.active_sessions}/${analytics.max_concurrent}`, color: '#00AEEF' },
            ].map((stat, i) => (
              <div key={i} className="bg-black border border-gray-800 p-4 text-center">
                <div className="font-mono text-3xl drop-shadow-[0_0_8px]" style={{ color: stat.color }}>{stat.value}</div>
                <div className="font-mono text-xs text-[#71717A] mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id} className="bg-[#121214] border border-gray-800 p-3 flex items-center justify-between">
                <div>
                  <div className="font-heading text-sm uppercase text-white">{u.name}</div>
                  <div className="font-mono text-xs text-[#A1A1AA]">{u.email}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm text-[#00AEEF]">LVL {u.level || 1}</div>
                  <div className="font-mono text-xs text-[#71717A]">{u.sessions_count || 0} games</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Profiles Tab */}
        {tab === 'profiles' && (
          <div className="space-y-3">
            <button
              data-testid="create-profile-btn"
              onClick={() => setShowProfileForm(!showProfileForm)}
              className="w-full bg-transparent border-2 border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black font-heading text-sm uppercase tracking-wider py-2 transition-all flex items-center justify-center gap-2"
            >
              <Plus size={16} /> Nouveau Profil
            </button>

            {showProfileForm && (
              <div className="bg-[#121214] border-2 border-[#39FF14]/30 p-4 space-y-3">
                <input data-testid="profile-name" value={pName} onChange={e => setPName(e.target.value)} className="bg-black border border-gray-700 text-white font-mono p-2 w-full rounded-none" placeholder="Nom" />
                <input data-testid="profile-desc" value={pDesc} onChange={e => setPDesc(e.target.value)} className="bg-black border border-gray-700 text-white font-mono p-2 w-full rounded-none" placeholder="Description" />
                <textarea data-testid="profile-personality" value={pPersonality} onChange={e => setPPersonality(e.target.value)} className="bg-black border border-gray-700 text-white font-mono p-2 w-full rounded-none" placeholder="Personnalité" rows={2} />
                <select data-testid="profile-tone" value={pTone} onChange={e => setPTone(e.target.value)} className="bg-black border border-gray-700 text-white font-mono p-2 w-full rounded-none">
                  {['polie', 'pressée', 'bête', 'méfiante', 'sèche', 'neutre'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="flex items-center gap-2">
                  <label className="font-mono text-sm text-[#A1A1AA]">Difficulté:</label>
                  <input type="number" min="1" max="5" value={pDifficulty} onChange={e => setPDifficulty(Number(e.target.value))} className="bg-black border border-gray-700 text-white font-mono p-2 w-20 rounded-none" />
                </div>
                <button data-testid="save-new-profile-btn" onClick={handleCreateProfile} className="bg-[#39FF14] text-black font-heading text-sm uppercase py-2 px-4 w-full">Créer</button>
              </div>
            )}

            {profiles.map((p) => (
              <div key={p.id} className="bg-[#121214] border border-gray-800 p-3 flex items-center justify-between">
                <div className="flex-1">
                  <div className="font-heading text-sm uppercase text-white">{p.name}</div>
                  <div className="font-mono text-xs text-[#A1A1AA]">{p.tone} - {'★'.repeat(p.difficulty)}</div>
                </div>
                {!p.is_default && (
                  <button data-testid={`delete-profile-${p.id}`} onClick={() => handleDeleteProfile(p.id)} className="text-[#E61C24] hover:text-[#FF333B] transition-colors p-1">
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Notifications Tab */}
        {tab === 'notifs' && (
          <div className="space-y-4">
            <div className="bg-[#121214] border-2 border-[#00AEEF]/30 p-4 space-y-3">
              <h3 className="font-heading text-sm uppercase text-[#00AEEF]">Envoyer une notification</h3>
              <select
                data-testid="notif-user-select"
                value={notifUserId}
                onChange={e => setNotifUserId(e.target.value)}
                className="bg-black border border-gray-700 text-white font-mono p-2 w-full rounded-none"
              >
                <option value="">Tous les utilisateurs</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
              </select>
              <textarea
                data-testid="notif-message"
                value={notifMsg}
                onChange={e => setNotifMsg(e.target.value)}
                className="bg-black border border-gray-700 text-white font-mono p-2 w-full rounded-none"
                placeholder="Message..."
                rows={2}
              />
              <button
                data-testid="send-notif-btn"
                onClick={handleSendNotification}
                className="bg-[#00AEEF] hover:bg-[#33C3FF] text-black font-heading text-sm uppercase py-2 px-4 w-full flex items-center justify-center gap-2"
              >
                <Send size={14} /> Envoyer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
